/**
 * Saved Searches client (STN client round 2026-08): the auto-saved search
 * list (recent chips + nav drawer), zero-LLM restore, and title editing.
 *
 * Server-backed by design — localStorage is banned in this app. Saved
 * searches are TOKEN-scoped (MAPS.SearchLog.PartnerTokenID; ratified
 * 2026-08-12): all users sharing one partner token see one list, but
 * sibling tokens of the same company do not see each other's searches.
 *
 * NgZone note: Apollo v4 delivers results OUTSIDE Angular's zone (bug class
 * of 617f096). This service stays zone-agnostic (plain promises); every
 * consumer applies results inside zone.run + detectChanges, matching the
 * SearchService consumption pattern.
 */
import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, Subject, firstValueFrom, type Observable } from 'rxjs';
import {
    MAPS_SAVED_SEARCHES,
    MAPS_SEARCH_RESTORE,
    MAPS_SEARCH_TITLE_UPDATE,
} from './maps-graphql/queries';
import type { MapsSavedSearch, MapsSavedSearchPage, MapsSearchRestoreResult } from '../models/maps.types';

@Injectable({ providedIn: 'root' })
export class SavedSearchesService {
    /** Shared first page + total — the header badge, recent chips, and drawer
     *  all read the same load (one query per Refresh, not one per consumer). */
    private readonly page$ = new BehaviorSubject<MapsSavedSearchPage | null>(null);
    /** Drawer open/closed (the header toggles it; app.component hosts it). */
    private readonly drawerOpen$ = new BehaviorSubject<boolean>(false);
    /** Restore clicks from the drawer. The search page subscribes; a click
     *  made from an admin route is parked in pendingRestoreId and consumed by
     *  the search page's ngOnInit after navigation. */
    private readonly restoreRequests$ = new Subject<string>();
    private pendingRestoreId: string | null = null;

    constructor(private readonly apollo: Apollo) {}

    get Page$(): Observable<MapsSavedSearchPage | null> {
        return this.page$.asObservable();
    }

    get DrawerOpen$(): Observable<boolean> {
        return this.drawerOpen$.asObservable();
    }

    get RestoreRequests$(): Observable<string> {
        return this.restoreRequests$.asObservable();
    }

    /** Reload the shared first page (after searches complete, title edits, drawer opens). */
    async Refresh(): Promise<void> {
        this.page$.next(await this.List(50, 0));
    }

    OpenDrawer(): void {
        this.drawerOpen$.next(true);
    }

    CloseDrawer(): void {
        this.drawerOpen$.next(false);
    }

    /** A drawer row was clicked: close the drawer and hand the id to the
     *  search page (live subscription on '/', pending pickup elsewhere). */
    RequestRestore(searchId: string): void {
        this.pendingRestoreId = searchId;
        this.CloseDrawer();
        this.restoreRequests$.next(searchId);
    }

    /** One-shot pickup for a restore requested before the search page mounted. */
    ConsumePendingRestore(): string | null {
        const id = this.pendingRestoreId;
        this.pendingRestoreId = null;
        return id;
    }

    /** Newest-first page + token-wide total. Default page covers the drawer's first screen. */
    async List(limit = 50, offset = 0): Promise<MapsSavedSearchPage> {
        const result = await firstValueFrom(
            this.apollo.query<{ mapsSavedSearches: MapsSavedSearchPage }>({
                query: MAPS_SAVED_SEARCHES,
                variables: { limit, offset },
                fetchPolicy: 'network-only',
            })
        );
        if (!result.data) throw new Error('Saved-search list returned no data');
        return result.data.mapsSavedSearches;
    }

    /** Reopen a saved search wholesale from its snapshot — no LLM cost, no TTL. */
    async Restore(searchId: string): Promise<MapsSearchRestoreResult> {
        const result = await firstValueFrom(
            this.apollo.query<{ mapsSearchRestore: MapsSearchRestoreResult }>({
                query: MAPS_SEARCH_RESTORE,
                variables: { searchId },
                fetchPolicy: 'network-only',
            })
        );
        if (!result.data) throw new Error('Restore returned no data');
        return result.data.mapsSearchRestore;
    }

    /** Rename a saved search; returns the updated row. */
    async UpdateTitle(searchId: string, title: string): Promise<MapsSavedSearch> {
        const result = await firstValueFrom(
            this.apollo.mutate<{ mapsSearchTitleUpdate: MapsSavedSearch }>({
                mutation: MAPS_SEARCH_TITLE_UPDATE,
                variables: { searchId, title },
            })
        );
        if (!result.data) throw new Error('Title update returned no data');
        return result.data.mapsSearchTitleUpdate;
    }
}
